import { Routine } from 'redux-saga-routines';
import { append, filter, equals, any, map, without, invertObj, find } from 'ramda';
import {
  toggleRequestBuilderRoutine,
  setReqPositionRoutine,
  fetchReqBuilderContactsRoutine,
  resetReqBuilderContactsRoutine,
  addReqBuilderCandidateRoutine,
  removeReqBuilderCandidateRoutine,
  changeReqCandidatesOwnerRoutine,
  changeRefCountersRoutine,
  fetchLastRefCountersRoutine,
  fetchReqBuilderSurveysRoutine,
  fetchPositionLastSurveyRoutine,
  setReqSurveyRoutine,
  setSendSMSToAllRoutine,
  changeMessageTextRoutine,
  createReqRefRoutine,
  disableRefCounterRoutine,
  setDisabledRefTypesRoutine,
  toggleAdvancedReqRoutine,
  requestAdditionalReferencesRoutine,
  updateSelectedCandidateRoutine,
  fetchRefReqEmailTemplatesRoutine,
  setReviewReferencesEnabledRoutine,
  setRefReqReferencesRoutine,
  addRefReqReferenceRoutine,
  removeRefReqReferenceRoutine,
  updateRefReqReferenceRoutine,
  setEmailTemplateRoutine
} from '../routines';
import { ISelectedPosition } from '../../../common/models/position/ISelectedPosition';
import { IRefReqContact } from '../../../common/models/contact/IRefReqContact';
import { IRefReqSelectedContact } from '../../../common/models/contact/IRefReqSelectedContact';
import { IUserInitials } from '../../../common/models/user/IUserInitials';
import { IRefReqCounters } from '../../../common/models/referenceRequest/IRefReqCounters';
import { ISurvey } from '../../../common/models/survey/ISurvey';
import { ISelectedSurvey } from '../../../common/models/survey/ISelectedSurvey';
import { saveSurveyRoutine } from '../../SurveyBuilder/routines';
import { ReferenceType } from '../../../common/enums/ReferenceType';
import { refCounterTypes } from '../../../common/helpers/refCountersHelper';
import { ILastRefReqCounters } from '../../../common/models/referenceRequest/ILastRefReqCounters';
import { IEmailTemplate } from '../../../common/models/email/IEmailTemplate';
import { showRequestBuilderModalRoutine } from '../../../routines/history';
import { INewContactInfo } from '../../../common/models/contact/INewContactInfo';
import { EmailType } from '../../../common/enums/EmailType';

export type RequestBuilderState = {
  isShown: boolean;
  loading: boolean;
  emailTemplate?: IEmailTemplate;
  defaultTemplates?: IEmailTemplate[];
  selectedPosition: ISelectedPosition | null;
  contacts: IRefReqContact[];
  totalContacts: number;
  candidates: IRefReqSelectedContact[];
  selectedOwner: IUserInitials;
  refCounters: IRefReqCounters;
  lastCounters: ILastRefReqCounters | null;
  disabledTypes: ReferenceType[];
  surveys: ISurvey[];
  totalSurveys: number;
  lastSurvey: ISelectedSurvey | null;
  selectedSurvey: ISelectedSurvey | null;
  sendSMS: boolean;
  reviewReferencesEnabled: boolean;
  isSent: boolean;
  isSaving: boolean;
  isAdvancedRequest: boolean;
  references: INewContactInfo[] | null;
};

const initialState: RequestBuilderState = {
  isShown: false,
  loading: false,
  emailTemplate: undefined,
  selectedPosition: null,
  contacts: [],
  totalContacts: 0,
  candidates: [],
  selectedOwner: {} as IUserInitials,
  refCounters: {} as IRefReqCounters,
  lastCounters: null,
  disabledTypes: [],
  surveys: [],
  totalSurveys: 0,
  lastSurvey: null,
  selectedSurvey: null,
  sendSMS: false,
  isSent: false,
  isSaving: false,
  isAdvancedRequest: false,
  reviewReferencesEnabled: false,
  references: null
};

export const requestBuilder = (
  state: RequestBuilderState = initialState,
  { type, payload }: Routine<any>
): RequestBuilderState => {
  switch (type) {
    case toggleRequestBuilderRoutine.TRIGGER:
      return {
        ...(payload ? state : initialState),
        isShown: payload
      };
    case showRequestBuilderModalRoutine.TRIGGER:
      return {
        ...state,
        loading: true
      };
    case setReqPositionRoutine.TRIGGER:
      return {
        ...state,
        selectedPosition: payload
      };
    case fetchReqBuilderContactsRoutine.SUCCESS:
      const contacts = payload.reset ? payload.items : [...state.contacts, ...payload.items];
      return {
        ...state,
        contacts,
        totalContacts: payload.total
      };
    case resetReqBuilderContactsRoutine.TRIGGER:
      return {
        ...state,
        contacts: [],
        totalContacts: 0
      };
    case addReqBuilderCandidateRoutine.SUCCESS:
      const exists = any((candidate: IRefReqSelectedContact) => (
        payload.id
          ? payload.id === candidate.id
          : (payload.email === candidate.email && payload.phoneNumber === candidate.phoneNumber)
      ))(state.candidates);
      const updatedCandidates = exists ? [...state.candidates] : append(payload, state.candidates);
      return {
        ...state,
        candidates: updatedCandidates,
        references: updatedCandidates.length > 1 ? null : state.references
      };
    case removeReqBuilderCandidateRoutine.TRIGGER:
      return {
        ...state,
        candidates: filter(cand => !equals(cand, payload), state.candidates)
      };
    case updateSelectedCandidateRoutine.SUCCESS:
      return {
        ...state,
        candidates: map(candidate => (equals(candidate, state.candidates[payload.index])
          ? { ...candidate, ...payload.data }
          : candidate), state.candidates)
      };
    case changeReqCandidatesOwnerRoutine.TRIGGER:
      return {
        ...state,
        selectedOwner: payload
      };
    case changeRefCountersRoutine.TRIGGER:
      return {
        ...state,
        refCounters: payload
      };
    case fetchLastRefCountersRoutine.SUCCESS:
      return {
        ...state,
        lastCounters: payload
      };
    case fetchReqBuilderSurveysRoutine.TRIGGER:
      return {
        ...state,
        loading: true
      };
    case fetchReqBuilderSurveysRoutine.FAILURE:
      return {
        ...state,
        loading: false
      };
    case fetchReqBuilderSurveysRoutine.SUCCESS:
      const surveys = payload.reset ? payload.items : [...state.surveys, ...payload.items];
      return {
        ...state,
        surveys,
        totalSurveys: payload.total,
        loading: false
      };
    case fetchPositionLastSurveyRoutine.SUCCESS:
      return {
        ...state,
        lastSurvey: payload,
        selectedSurvey: state.selectedSurvey || payload
      };
    case setReqSurveyRoutine.TRIGGER:
      return {
        ...state,
        selectedSurvey: payload
      };
    case setSendSMSToAllRoutine.TRIGGER:
      return {
        ...state,
        sendSMS: payload
      };
    case setReviewReferencesEnabledRoutine.TRIGGER:
      return {
        ...state,
        reviewReferencesEnabled: payload
      };
    case setEmailTemplateRoutine.TRIGGER:
      return {
        ...state,
        emailTemplate: payload
      };
    case changeMessageTextRoutine.TRIGGER: {
      return {
        ...state,
        emailTemplate: { ...(state.emailTemplate as IEmailTemplate), body: payload }
      };
    }
    case createReqRefRoutine.SUCCESS:
      return {
        ...state,
        isSent: true,
        isSaving: false
      };
    case createReqRefRoutine.FAILURE:
      return {
        ...state,
        isSaving: false
      };
    case createReqRefRoutine.TRIGGER:
      return {
        ...state,
        isSaving: true
      };
    case saveSurveyRoutine.SUCCESS:
      if (!state.isShown) {
        return state;
      }

      return {
        ...state,
        selectedSurvey: { id: payload.id, name: payload.name },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        surveys: map(survey => (survey.id === payload.id
          ? { ...survey, name: payload.name }
          : survey
        ), state.surveys)
      };
    case disableRefCounterRoutine.TRIGGER:
      const key = invertObj(refCounterTypes)[payload];
      return {
        ...state,
        disabledTypes: any(refType => refType === payload, state.disabledTypes)
          ? without([payload], state.disabledTypes)
          : append(payload, state.disabledTypes),
        refCounters: {
          ...state.refCounters,
          [key]: 0
        }
      };
    case setDisabledRefTypesRoutine.TRIGGER:
      return {
        ...state,
        disabledTypes: payload
      };
    case toggleAdvancedReqRoutine.TRIGGER:
      return {
        ...state,
        isAdvancedRequest: payload
      };
    case requestAdditionalReferencesRoutine.TRIGGER:
      return {
        ...state,
        isShown: true,
        candidates: payload.candidates,
        selectedPosition: payload.selectedPosition
      };
    case fetchRefReqEmailTemplatesRoutine.SUCCESS:
      return {
        ...state,
        emailTemplate: find(template => template.type === EmailType.CandidateRefRequest, payload),
        defaultTemplates: payload
      };
    case setRefReqReferencesRoutine.TRIGGER:
      return {
        ...state,
        references: payload
      };
    case addRefReqReferenceRoutine.SUCCESS:
      const currentReferences = [...(state.references as INewContactInfo[])];
      const refExists = any((ref: INewContactInfo) => (
        ref.email === payload.email && ref.phoneNumber === payload.phoneNumber
      ))(currentReferences);

      return {
        ...state,
        references: refExists ? currentReferences : append(payload, currentReferences)
      };
    case removeRefReqReferenceRoutine.TRIGGER:
      return {
        ...state,
        references: filter(ref => !equals(ref, payload), state.references as INewContactInfo[])
      };
    case updateRefReqReferenceRoutine.SUCCESS:
      return {
        ...state,
        references: map(ref => (equals(ref, (state.references as INewContactInfo[])[payload.index])
          ? { ...ref, ...payload.data }
          : ref), state.references as INewContactInfo[])
      };
    default:
      return state;
  }
};
